import {
  type ProductizedServicePriceInterval,
  type ProductizedServicePriceType,
} from '@/__generated__/api.js';
import { type ServiceNode } from '@/types/services.js';
import { type ExcludeRelayFutureAddedValue } from '@/types/utils.js';
import { formatCurrency } from '@/utilities/currency.js';
import { truncateString } from '@/utilities/strings.js';

const getPriceWithInterval = ({
  amount,
  interval,
  type,
}: {
  amount?: number | null;
  interval?: ProductizedServicePriceInterval | null;
  type: ProductizedServicePriceType;
}) => {
  if (!amount) return null;
  if (interval === '%future added value' || type === '%future added value')
    return null;

  const friendlyAmount = formatCurrency(amount, {
    maximumFractionDigits: 0,
    minimumFractionDigits: 0,
    style: 'decimal',
  });
  const resolvedInterval = interval && type === 'RATE' ? ` / ${interval}` : '';
  return `$${friendlyAmount}` + resolvedInterval;
};

const friendlyTypeMap: Record<
  ExcludeRelayFutureAddedValue<ProductizedServicePriceType>,
  string
> = {
  CONTACT_FOR_PRICING: 'Contact for Pricing',
  FIXED_PRICE: 'Fixed Price',
  RATE: 'Rate',
};

const getServiceTypeLabel = (type: ServiceNode['price']['type']) => {
  if (type === '%future added value') return null;

  return friendlyTypeMap[type];
};

export const getServiceTimelineLabel = (
  timeline: ServiceNode['price']['timeline'],
) => {
  return timeline ? truncateString(timeline) : null;
};

const isContactForPricingServiceType = ({
  amount,
  type,
}: {
  amount: ServiceNode['price']['amount'];
  type: ServiceNode['price']['type'];
}) => {
  return type === 'CONTACT_FOR_PRICING' || Number.isNaN(amount);
};

const getTimeline = (timeline?: string | null) => {
  return timeline ? `${truncateString(timeline)}` : '';
};

const getTypeWithTimeline = ({
  timeline,
  type,
}: {
  timeline?: string | null;
  type: ProductizedServicePriceType;
}) => {
  const friendlyType = getServiceTypeLabel(type);
  const resolvedTimeline = timeline ? `, ${getTimeline(timeline)}` : '';
  return friendlyType + resolvedTimeline;
};

export const getServicePricingDetails = (price: ServiceNode['price']) => {
  const priceWithInterval = getPriceWithInterval({ ...price });
  const typeWithTimeline = getTypeWithTimeline({ ...price });
  const timeline = getTimeline(price.timeline);
  const isContactForPricing = isContactForPricingServiceType({
    amount: price.amount,
    type: price.type,
  });
  const isFixedPrice = price.type === 'FIXED_PRICE';
  const isFreeService = isFixedPrice && price.amount === 0;
  const isVariableRate = price.type === 'RATE';

  return {
    isContactForPricing,
    isFixedPrice,
    isFreeService,
    isVariableRate,
    priceWithInterval,
    timeline,
    typeWithTimeline,
  };
};

export const getService = (node: ServiceNode) => {
  const {
    isContactForPricing,
    isFixedPrice,
    isFreeService,
    isVariableRate,
    priceWithInterval,
    timeline,
    typeWithTimeline,
  } = getServicePricingDetails(node.price);

  const linkedProjects =
    node.portfolioProjects.edges.length >= 3
      ? node.portfolioProjects.edges.slice(0, 3)
      : node.portfolioProjects.edges;

  return {
    isContactForPricing,
    isFixedPrice,
    isFreeService,
    isVariableRate,
    linkedProjects,
    priceWithInterval,
    timeline,
    typeWithTimeline,
  };
};
